import type {
  ProjectPlannerDependencyLink,
  ProjectPlannerDependencyLinkType,
  ProjectPlannerItem,
} from '../../../types/database';
import type { Constructor } from './mixins';

const DEFAULT_PLANNER_COLOR = 'var(--color-brand)';

const toDateOnly = (value?: string): string => {
  if (!value) return new Date().toISOString().slice(0, 10);
  return value.slice(0, 10);
};

const parseDateOnly = (value: string): Date => new Date(`${toDateOnly(value)}T00:00:00`);

const addDays = (value: string, days: number): string => {
  const date = parseDateOnly(value);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const diffInDays = (start: string, end: string): number => {
  const startDate = parseDateOnly(start);
  const endDate = parseDateOnly(end);
  return Math.round((endDate.getTime() - startDate.getTime()) / 86400000);
};

const hasDisplayOrder = (item: ProjectPlannerItem): item is ProjectPlannerItem & { displayOrder: number } =>
  typeof item.displayOrder === 'number' && Number.isFinite(item.displayOrder);

const comparePlannerItems = (a: ProjectPlannerItem, b: ProjectPlannerItem): number => {
  const aHasOrder = hasDisplayOrder(a);
  const bHasOrder = hasDisplayOrder(b);

  if (aHasOrder && bHasOrder && a.displayOrder !== b.displayOrder) {
    return a.displayOrder - b.displayOrder;
  }

  if (a.startDate !== b.startDate) return a.startDate.localeCompare(b.startDate);
  if (a.endDate !== b.endDate) return a.endDate.localeCompare(b.endDate);
  return a.title.localeCompare(b.title);
};

const maxDateString = (values: string[]): string | null => {
  if (values.length === 0) return null;
  return values.reduce((latest, current) => (current > latest ? current : latest), values[0]);
};

const normalizeLinkType = (value: unknown): ProjectPlannerDependencyLinkType => {
  const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return normalized === 'SS' || normalized === 'FF' || normalized === 'SF'
    ? normalized
    : 'FS';
};

const dedupePredecessorLinks = (links: ProjectPlannerDependencyLink[]): ProjectPlannerDependencyLink[] => {
  const byPredecessorId = new Map<string, ProjectPlannerDependencyLinkType>();
  links.forEach(link => {
    const predecessorId = String(link?.predecessorId || '').trim();
    if (!predecessorId) return;
    const nextType = normalizeLinkType(link?.linkType);
    const existingType = byPredecessorId.get(predecessorId);
    if (!existingType || (existingType === 'FS' && nextType !== 'FS')) {
      byPredecessorId.set(predecessorId, nextType);
    }
  });

  return Array.from(byPredecessorId.entries()).map(([predecessorId, linkType]) => ({
    predecessorId,
    linkType,
  }));
};

const normalizePredecessorLinks = (item: ProjectPlannerItem): ProjectPlannerDependencyLink[] => {
  const normalizedLinks: ProjectPlannerDependencyLink[] = [];

  if (Array.isArray(item.predecessorLinks)) {
    item.predecessorLinks.forEach(link => {
      const predecessorId = String(link?.predecessorId || '').trim();
      if (!predecessorId) return;
      normalizedLinks.push({
        predecessorId,
        linkType: normalizeLinkType(link?.linkType),
      });
    });
  }

  if (Array.isArray(item.predecessorIds)) {
    item.predecessorIds.forEach(id => {
      const predecessorId = String(id || '').trim();
      if (!predecessorId) return;
      normalizedLinks.push({
        predecessorId,
        linkType: 'FS',
      });
    });
  }

  return dedupePredecessorLinks(normalizedLinks);
};

export const withProjectPlanner = <TBase extends Constructor<any>>(Base: TBase) =>
  class ProjectPlanner extends Base {
    private normalizePlannerDates(startDate?: string, endDate?: string, itemType?: string): { startDate: string; endDate: string } {
      const normalizedStart = toDateOnly(startDate);
      const normalizedEnd = toDateOnly(endDate || startDate);

      if (itemType === 'milestone') {
        return { startDate: normalizedStart, endDate: normalizedStart };
      }

      if (normalizedEnd < normalizedStart) {
        return { startDate: normalizedStart, endDate: normalizedStart };
      }

      return { startDate: normalizedStart, endDate: normalizedEnd };
    }

    private normalizeProgress(progress: number | undefined, status: string | undefined): number {
      if (status === 'completed') return 100;
      const value = typeof progress === 'number' && Number.isFinite(progress) ? progress : 0;
      return Math.max(0, Math.min(100, Math.round(value)));
    }

    private normalizePlannerItem(item: ProjectPlannerItem): ProjectPlannerItem {
      const normalizedDates = this.normalizePlannerDates(item.startDate, item.endDate, item.itemType);
      const predecessorLinks = normalizePredecessorLinks(item);
      const predecessorIds = predecessorLinks.map(link => link.predecessorId);
      const attachments = Array.isArray(item.attachments)
        ? item.attachments.filter(Boolean)
        : [];
      const displayOrder = Number(item.displayOrder);
      const normalizedDisplayOrder = Number.isFinite(displayOrder)
        ? Math.max(0, Math.floor(displayOrder))
        : undefined;

      return {
        ...item,
        startDate: normalizedDates.startDate,
        endDate: normalizedDates.endDate,
        progress: this.normalizeProgress(item.progress, item.status),
        color: item.color || DEFAULT_PLANNER_COLOR,
        predecessorIds,
        predecessorLinks,
        attachments,
        displayOrder: normalizedDisplayOrder,
      };
    }

    private async autoScheduleProject(projectId: string): Promise<void> {
      const projectItems = this.getProjectPlannerItemsByProject(projectId).map(item => ({ ...item }));
      if (projectItems.length === 0) return;

      const itemsById = new Map(projectItems.map(item => [item.id, item]));
      const changedItems = new Map<string, ProjectPlannerItem>();

      let changed = true;
      let loops = 0;
      while (changed && loops < 100) {
        changed = false;
        loops++;

        for (const item of projectItems) {
          const dependencyLinks = Array.isArray(item.predecessorLinks) && item.predecessorLinks.length > 0
            ? item.predecessorLinks
            : (item.predecessorIds || []).map(predecessorId => ({ predecessorId, linkType: 'FS' as const }));
          if (dependencyLinks.length === 0) continue;

          const activeDependencies = dependencyLinks
            .map(link => {
              const predecessorId = String(link?.predecessorId || '').trim();
              if (!predecessorId) return null;
              const predecessor = itemsById.get(predecessorId);
              if (!predecessor) return null;
              return {
                predecessor,
                linkType: normalizeLinkType(link?.linkType),
              };
            })
            .filter((value): value is { predecessor: ProjectPlannerItem; linkType: ProjectPlannerDependencyLinkType } => !!value);

          if (activeDependencies.length === 0) continue;

          const duration = Math.max(0, diffInDays(item.startDate, item.endDate));
          const requiredStartCandidates: string[] = [];
          const requiredEndCandidates: string[] = [];

          activeDependencies.forEach(({ predecessor, linkType }) => {
            if (linkType === 'FS') {
              requiredStartCandidates.push(addDays(predecessor.endDate, 1));
            } else if (linkType === 'SS') {
              requiredStartCandidates.push(predecessor.startDate);
            } else if (linkType === 'FF') {
              requiredEndCandidates.push(predecessor.endDate);
            } else if (linkType === 'SF') {
              requiredEndCandidates.push(predecessor.startDate);
            }
          });

          let requiredStart = item.startDate;
          const latestStartConstraint = maxDateString(requiredStartCandidates);
          if (latestStartConstraint && latestStartConstraint > requiredStart) {
            requiredStart = latestStartConstraint;
          }

          const latestEndConstraint = maxDateString(requiredEndCandidates);
          if (latestEndConstraint) {
            const startFromEndConstraint = addDays(latestEndConstraint, -duration);
            if (startFromEndConstraint > requiredStart) {
              requiredStart = startFromEndConstraint;
            }
          }

          if (item.startDate < requiredStart) {
            const nextStart = requiredStart;
            const nextEnd = addDays(nextStart, duration);

            item.startDate = nextStart;
            item.endDate = nextEnd;
            changedItems.set(item.id, { ...item });
            changed = true;
          }
        }
      }

      if (changedItems.size === 0) return;

      for (const [id, item] of changedItems.entries()) {
        const normalized = this.normalizePlannerItem(item);
        this.db.projectPlannerItems[id] = normalized;
        await this.saveEntityToBackend('projectPlannerItems', id, normalized, false);
      }

      this.saveToLocalStorage();
      this.notifyListeners();
    }

    public getProjectPlannerItems(): ProjectPlannerItem[] {
      return Object.values(this.db.projectPlannerItems)
        .map((item: unknown) => this.normalizePlannerItem(item as ProjectPlannerItem))
        .sort(comparePlannerItems);
    }

    public getProjectPlannerItemsByProject(projectId: string): ProjectPlannerItem[] {
      return this.getProjectPlannerItems().filter(item => item.projectId === projectId);
    }

    public getProjectPlannerItem(id: string): ProjectPlannerItem | undefined {
      const item = this.db.projectPlannerItems[id];
      return item ? this.normalizePlannerItem(item) : undefined;
    }

    public async createProjectPlannerItem(
      item: Omit<ProjectPlannerItem, 'id' | 'createdAt' | 'updatedAt'>
    ): Promise<ProjectPlannerItem> {
      const now = new Date().toISOString();
      const projectItems = this.getProjectPlannerItemsByProject(item.projectId);
      const itemDisplayOrder = Number(item.displayOrder);
      const normalizedItemDisplayOrder = Number.isFinite(itemDisplayOrder)
        ? Math.max(0, Math.floor(itemDisplayOrder))
        : undefined;
      const nextDisplayOrder = projectItems.reduce((max, plannerItem) => {
        const orderCandidate = hasDisplayOrder(plannerItem) ? plannerItem.displayOrder : max + 1;
        return Math.max(max, orderCandidate);
      }, -1) + 1;

      const newItem = this.normalizePlannerItem({
        ...item,
        displayOrder: normalizedItemDisplayOrder ?? nextDisplayOrder,
        id: this.generateId(),
        createdAt: now,
        updatedAt: now,
      });

      await this.saveEntityToBackend('projectPlannerItems', newItem.id, newItem, true);
      this.db.projectPlannerItems[newItem.id] = newItem;
      this.saveToLocalStorage();
      this.notifyListeners();

      await this.autoScheduleProject(newItem.projectId);
      return this.getProjectPlannerItem(newItem.id)!;
    }

    public async updateProjectPlannerItem(
      id: string,
      updates: Partial<ProjectPlannerItem>
    ): Promise<ProjectPlannerItem | undefined> {
      const existing = this.db.projectPlannerItems[id];
      if (!existing) return undefined;

      const merged = this.normalizePlannerItem({
        ...existing,
        ...updates,
        id,
        updatedAt: new Date().toISOString(),
      });

      await this.saveEntityToBackend('projectPlannerItems', id, merged, false);
      this.db.projectPlannerItems[id] = merged;
      this.saveToLocalStorage();
      this.notifyListeners();

      await this.autoScheduleProject(merged.projectId);
      return this.getProjectPlannerItem(id);
    }

    public async reorderProjectPlannerItems(
      projectId: string,
      orderedItemIds: string[],
      updatedBy?: string
    ): Promise<void> {
      const projectItems = this.getProjectPlannerItemsByProject(projectId);
      if (projectItems.length === 0) return;

      const validIds = orderedItemIds.filter(id => {
        const item = this.db.projectPlannerItems[id];
        return !!item && item.projectId === projectId;
      });
      const missingIds = projectItems.map(item => item.id).filter(id => !validIds.includes(id));
      const completeOrder = [...validIds, ...missingIds];
      const now = new Date().toISOString();

      const saves: Promise<void>[] = [];
      let changed = false;

      completeOrder.forEach((id, index) => {
        const existing = this.db.projectPlannerItems[id];
        if (!existing) return;

        const existingDisplayOrder = Number(existing.displayOrder);
        const currentOrder = Number.isFinite(existingDisplayOrder) ? Math.max(0, Math.floor(existingDisplayOrder)) : undefined;
        if (currentOrder === index && (!updatedBy || existing.updatedBy === updatedBy)) return;

        const updated = this.normalizePlannerItem({
          ...existing,
          displayOrder: index,
          updatedBy: updatedBy ?? existing.updatedBy,
          updatedAt: now,
        });
        this.db.projectPlannerItems[id] = updated;
        saves.push(this.saveEntityToBackend('projectPlannerItems', id, updated, false));
        changed = true;
      });

      if (!changed) return;

      this.saveToLocalStorage();
      this.notifyListeners();
      await Promise.all(saves);
    }

    public async deleteProjectPlannerItem(id: string): Promise<boolean> {
      const item = this.db.projectPlannerItems[id];
      if (!item) return false;

      try {
        await this.deleteEntityFromBackend('projectPlannerItems', id);
      } catch (error) {
        console.error('❌ Error deleting planner item from backend:', error);
      }

      delete this.db.projectPlannerItems[id];
      this.saveToLocalStorage();
      this.notifyListeners();

      await this.autoScheduleProject(item.projectId);
      return true;
    }
  };
